import {
    Component,
    EventEmitter,
    Injectable,
    OnDestroy,
    TemplateRef
} from '@angular/core';
import {
    embedded,
    hideModalBackground,
    showModalBackground
} from 'angular/utils/config';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';
import { Observable } from 'rxjs';

@Injectable()
export class ConfirmModal {
    lastComponent: ConfirmModalComponent = null;

    constructor(private bsModalService: BsModalService) {
        if (!embedded) return;

        const reactBody = window.parent.document.body;

        const onReactClick = () => {
            if (!this.lastComponent) return;
            this.lastComponent.close();
        };

        bsModalService.onShow.subscribe(() => {
            showModalBackground();
            reactBody.addEventListener('click', onReactClick);
        });

        bsModalService.onShown.subscribe(() => {
            window.parent.scrollTo(0, 0);
        });

        bsModalService.onHide.subscribe(() => {
            hideModalBackground();
            reactBody.removeEventListener('click', onReactClick);
        });
    }

    fromText(
        text: string,
        title: string = 'Are you sure?'
    ): Observable<boolean> {
        return this.instantiateModal({ text, title });
    }

    fromTemplate(
        textTemplate: TemplateRef<any>,
        title: string = 'Are you sure?'
    ): Observable<boolean> {
        return this.instantiateModal({ textTemplate, title });
    }

    private instantiateModal(
        initialState:
            | { textTemplate: TemplateRef<any>; title: string }
            | { text: string; title: string }
    ) {
        const component = this.bsModalService.show(ConfirmModalComponent, {
            initialState: initialState
        }).content as ConfirmModalComponent;
        this.lastComponent = component;
        return component.onClose.asObservable();
    }
}

@Component({
    template: `
        <div class="modal-content">
            <div class="modal-header">
                <button type="button" class="close" (click)="close(null)">
                    <i class="fa fa-close"></i>
                </button>
                <h4 class="modal-title">{{ title }}</h4>
            </div>
            <div class="modal-body">
                <ng-container *ngIf="text; else textTemplate">
                    {{ text }}
                </ng-container>
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-light"
                    (click)="close(false)"
                >
                    No, I do not want to.
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    (click)="close(true)"
                >
                    Yes, I want to.
                </button>
            </div>
        </div>
    `
})
export class ConfirmModalComponent implements OnDestroy {
    public text: string;
    public textTemplate: TemplateRef<any>;
    public title: string;
    /** I.e. not by an outside click or Escape key. */
    private explicitlyClosed: boolean = false;

    onClose = new EventEmitter<boolean>();

    constructor(private bsModalRef: BsModalRef) {}

    close(res?: boolean) {
        this.explicitlyClosed = true;
        !!res ? this.onClose.emit(res) : this.onClose.error(res);
        this.onClose.complete();
        this.bsModalRef.hide();
    }

    ngOnDestroy(): void {
        !this.explicitlyClosed && this.onClose.error(null);
    }
}
